import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, ExclamationCircleIcon } from '@heroicons/react/outline';

/**
 * SignOffTriageLetterDialog
 * -------------------------
 * The clinician's mandatory double-confirmation sign-off for a rendered triage
 * outcome letter (TD-249). Sign-off sits between rendering and sending:
 *
 *   1. The clinician confirms they have reviewed the final rendered letter
 *      (explicit checkbox — first confirmation).
 *   2. They are then asked to confirm again that sign-off makes the letter
 *      final and irreversible (second confirmation).
 *
 * After sign-off the letter can no longer be regenerated, re-rendered or
 * edited by anyone — a mistake found later needs a clinician-owned new
 * decision/letter, never an administrative correction. Only a signed-off
 * letter can be sent (the send itself may be performed by a support user).
 */
export default function SignOffTriageLetterDialog({
  open,
  onOpenChange,
  patientName,
  signing = false,
  onConfirm,
}) {
  // Step 1: reviewed-the-letter confirmation; step 2: final irreversibility
  // confirmation. Both reset whenever the dialog is (re)opened.
  const [reviewedConfirmed, setReviewedConfirmed] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (open) {
      setReviewedConfirmed(false);
      setStep(1);
    }
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-start justify-between p-5 pb-3">
            <div className="text-left">
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Sign off triage outcome letter
              </Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500 mt-0.5">
                {patientName ? `${patientName} — ` : ''}signing off finalises the letter so it
                can be sent.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {step === 1 ? (
            <div className="px-5 pb-2 text-left">
              <p className="text-sm text-gray-700">
                Review the final rendered letter before signing it off. Once signed off it is
                final: it cannot be regenerated, re-rendered or edited by anyone.
              </p>
              <label className="mt-4 flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reviewedConfirmed}
                  onChange={(e) => setReviewedConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-800">
                  I have reviewed the final rendered letter and I approve its content.
                </span>
              </label>
            </div>
          ) : (
            <div className="px-5 pb-2 text-left">
              <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <ExclamationCircleIcon
                  className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600"
                  aria-hidden="true"
                />
                <p className="text-sm text-amber-800">
                  Are you sure? Signing off makes this letter <strong>final and irreversible</strong>.
                  It will become available to send and can no longer be changed.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 p-5 pt-3">
            {step === 1 ? (
              <>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  disabled={!reviewedConfirmed}
                  onClick={() => setStep(2)}
                  className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sign off letter
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={signing}
                  onClick={() => setStep(1)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={signing}
                  onClick={() => onConfirm && onConfirm()}
                  className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {signing ? 'Signing off…' : 'Confirm sign-off'}
                </button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
